import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useDemo } from "../context/DemoContext";
import { submitDemoRequest } from "../lib/api";
import { LogoMark } from "./Logo";

const ROLES = ["Owner", "Executive Chef", "Head Chef", "Sous Chef", "Operations", "General Manager", "Other"];
const LOCATIONS = ["1", "2–5", "6–15", "16–50", "50+"];

const EMPTY = {
  full_name: "",
  restaurant_name: "",
  role: "",
  email: "",
  phone: "",
  locations: "",
  country: "",
  message: "",
};

const fieldBase =
  "bg-white border-[var(--mo-line)] text-[#1b1a18] placeholder:text-[#9a958c] focus-visible:ring-[#EA6A43]";

export const DemoModal = () => {
  const { open, setOpen } = useDemo();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const validate = () => {
    if (!form.full_name.trim()) return "Please enter your full name.";
    if (!form.restaurant_name.trim()) return "Please enter your restaurant name.";
    if (!form.role) return "Please select your role.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email.";
    if (form.phone.trim().length < 3) return "Please enter a phone number.";
    if (!form.locations) return "Please select the number of locations.";
    if (!form.country.trim()) return "Please enter your country.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await submitDemoRequest(form);
      setDone(true);
    } catch (_) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) {
      setTimeout(() => {
        setDone(false);
        setForm(EMPTY);
      }, 250);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="demo-modal"
        className="mo-hide-scrollbar max-h-[92vh] overflow-y-auto border-[var(--mo-line)] bg-[#f5f2ea] p-0 sm:max-w-xl"
      >
        {done ? (
          <motion.div
            data-testid="demo-success"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center px-8 py-20 text-center"
          >
            <LogoMark size={34} className="text-[#1b1a18]" />
            <div className="mo-rule mt-8 w-24" />
            <p className="mt-8 font-display text-2xl font-bold tracking-tight text-[#1b1a18]">
              Your place is set.
            </p>
            <p className="mt-3 max-w-sm font-body text-sm leading-relaxed text-[#6b665e]">
              Thanks. Someone from our team will be in touch soon to set up your demo.
            </p>
            <button
              data-testid="demo-success-close"
              onClick={() => handleOpenChange(false)}
              className="mt-10 font-body text-[11px] uppercase tracking-[0.24em] text-[#EA6A43] transition-opacity hover:opacity-70"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <div className="px-6 py-7 sm:px-8 sm:py-8">
            <DialogHeader className="text-left">
              <div className="mb-2 flex items-center gap-2">
                <LogoMark size={20} className="text-[#1b1a18]" />
                <span className="font-body text-[10px] uppercase tracking-[0.28em] text-[#EA6A43]">
                  Book a Demo
                </span>
              </div>
              <DialogTitle className="font-display text-2xl font-bold tracking-tight text-[#1b1a18]">
                See MiseOne in your kitchen.
              </DialogTitle>
              <DialogDescription className="font-body text-sm text-[#6b665e]">
                Tell us a little about your kitchen and we’ll set up a time.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-[#6b665e]">Full Name</Label>
                  <Input data-testid="demo-full-name" value={form.full_name} onChange={set("full_name")} placeholder="Marco Pierre" className={fieldBase} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#6b665e]">Restaurant Name</Label>
                  <Input data-testid="demo-restaurant" value={form.restaurant_name} onChange={set("restaurant_name")} placeholder="La Maison" className={fieldBase} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#6b665e]">Role</Label>
                  <Select value={form.role} onValueChange={set("role")}>
                    <SelectTrigger data-testid="demo-role" className={fieldBase}>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent className="border-[var(--mo-line)] bg-white text-[#1b1a18]">
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r} className="focus:bg-[#f5f2ea]">{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#6b665e]">Email</Label>
                  <Input data-testid="demo-email" type="email" value={form.email} onChange={set("email")} placeholder="chef@restaurant.com" className={fieldBase} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#6b665e]">Phone Number</Label>
                  <Input data-testid="demo-phone" value={form.phone} onChange={set("phone")} placeholder="+33 6 12 34 56 78" className={fieldBase} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#6b665e]">Number of Locations</Label>
                  <Select value={form.locations} onValueChange={set("locations")}>
                    <SelectTrigger data-testid="demo-locations" className={fieldBase}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="border-[var(--mo-line)] bg-white text-[#1b1a18]">
                      {LOCATIONS.map((l) => (
                        <SelectItem key={l} value={l} className="focus:bg-[#f5f2ea]">{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[#6b665e]">Country</Label>
                <Input data-testid="demo-country" value={form.country} onChange={set("country")} placeholder="France" className={fieldBase} />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[#6b665e]">Message (optional)</Label>
                <Textarea data-testid="demo-message" value={form.message} onChange={set("message")} placeholder="Tell us about your kitchen…" rows={3} className={fieldBase} />
              </div>

              <button
                type="submit"
                data-testid="demo-submit"
                disabled={submitting}
                className="mo-cta mt-2 inline-flex w-full items-center justify-center rounded-full bg-[#EA6A43] px-8 py-3.5 font-body text-sm font-medium tracking-wide text-white disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Book a Demo"}
              </button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
